import slugify from 'slugify';
import validator from 'validator';
import camelcase from 'camelcase';

/**
 * Converts a camelCase string to a regular space-separated string.
 * Optionally converts the result to uppercase or sentence case.
 *
 * @param target - The camelCase string to convert
 * @param toUpper - If true, converts to uppercase. If false, uses sentence case (first letter capitalized, rest lowercase)
 * @returns The converted string
 *
 * @example
 * camelCaseToRegular('thisIsCamelCase') // 'THIS IS CAMEL CASE'
 * camelCaseToRegular('thisIsCamelCase', false) // 'This is camel case'
 */
export const camelCaseToRegular = (target: string, toUpper = true) => {
  let newString = target.split(/(?=[A-Z])/g).join(' ');

  if (toUpper) {
    newString = newString.toUpperCase();
  } else {
    // Capitalize only the first letter, rest lowercase (sentence case)
    newString = newString.charAt(0).toUpperCase() + newString.slice(1).toLowerCase();
  }

  return newString;
};

/**
 * Converts a regular string or array of strings to camelCase.
 *
 * @param target - The string or array of strings to convert
 * @returns The camelCase string
 *
 * @example
 * regularToCamelCase('this is regular') // 'thisIsRegular'
 * regularToCamelCase(['this', 'is', 'regular']) // 'thisIsRegular'
 */
export const regularToCamelCase = (target: string | readonly string[]) =>
  camelcase(target);

/**
 * Truncates the target string after the specified length and appends an ellipsis.
 *
 * @param target - The string to truncate
 * @param length - The maximum length before truncation (default: 45)
 * @returns The truncated string with ellipsis appended
 *
 * @example
 * truncateWithEllipsis('This is a very long string', 10) // 'This is a ...'
 */
export const truncateWithEllipsis = (target: string, length = 45) => {
  return target.slice(0, length) + '...';
};

export const slugifyOptions = {
  lower: true,
  remove: /[$*_+~.,()'"!:@]/g,
};
slugify.extend({
  '|': '',
  $: '%24',
  '%': '%',
});

/**
 * Converts a string to a URL-friendly slug format.
 * Removes special characters and converts to lowercase.
 *
 * @param value - The string to slugify
 * @returns The slugified string
 *
 * @example
 * slugifyInput('Hello World!') // 'hello-world'
 * slugifyInput('This & That') // 'this-that'
 */
export const slugifyInput = (value: string) => {
  return slugify(value, slugifyOptions);
};

export const formatDateOptions: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: '2-digit',
  year: 'numeric',
};

/**
 * Formats a date input using Intl.DateTimeFormat.
 *
 * @param input - The date to format (string, Date object, or timestamp)
 * @param dateOptions - Optional Intl.DateTimeFormatOptions to customize the format
 * @returns The formatted date string
 *
 * @example
 * formatDate(new Date()) // 'January 15, 2024'
 * formatDate('2024-01-15', { month: 'short' }) // 'Jan 15, 2024'
 */
export const formatDate = (
  input: string | Date | number,
  dateOptions: Intl.DateTimeFormatOptions = formatDateOptions,
) => {
  try {
    const date = new Date(input);
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString(undefined, dateOptions);
  } catch {
    return '';
  }
};

/**
 * Formats a date that may come from APIs as ISO strings, epoch numbers, or numeric strings
 * (seconds vs milliseconds). Use when the shape of `createdAt` / order timestamps is not guaranteed.
 *
 * @param input - Date-like value or null/undefined
 * @param dateOptions - Optional Intl.DateTimeFormatOptions (same as {@link formatDate})
 * @returns Locale date string, or empty string if missing or unparseable
 */
export const formatDateFromUnknown = (
  input: string | number | Date | null | undefined,
  dateOptions: Intl.DateTimeFormatOptions = formatDateOptions,
): string => {
  if (input == null || input === '') {
    return '';
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed);
      const asMs = trimmed.length === 10 ? numeric * 1000 : numeric;
      return formatDate(asMs, dateOptions);
    }
  }

  return formatDate(input, dateOptions);
};

/**
 * Formats a numeric timestamp string as a 12-hour time string.
 *
 * @param numString - Numeric timestamp string (e.g., '1609459200000')
 * @returns The formatted date and time string in 12-hour format, or empty string if invalid
 *
 * @example
 * formatDate12Hr('1609459200000') // '1/1/2021, 12:00 AM'
 */
export const formatDate12Hr = (numString: string) => {
  try {
    const timestamp = parseInt(numString, 10);
    if (isNaN(timestamp)) {
      return '';
    }
    const formattedDate = new Date(timestamp);
    if (isNaN(formattedDate.getTime())) {
      return '';
    }
    return formattedDate.toLocaleString('en-US', {
      hour12: true,
      hour: 'numeric',
      minute: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Formats a numeric timestamp string as a date-only string (no time).
 *
 * @param numString - Numeric timestamp string (e.g., '1609459200000')
 * @returns The formatted date string, or empty string if invalid
 *
 * @example
 * formatDateOnly('1609459200000') // 'January 1, 2021'
 */
export const formatDateOnly = (numString: string) => {
  try {
    const timestamp = parseInt(numString, 10);
    if (isNaN(timestamp)) {
      return '';
    }
    const formattedDate = new Date(timestamp);
    if (isNaN(formattedDate.getTime())) {
      return '';
    }
    return formattedDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Extracts the hostname from a URL string.
 * Returns the original string if the URL is invalid or extraction fails.
 *
 * @param urlString - The URL string to extract the hostname from
 * @returns The hostname, or the original string if invalid
 *
 * @example
 * hostFromUrl('https://example.com/path') // 'example.com'
 * hostFromUrl('invalid-url') // 'invalid-url'
 */
export const hostFromUrl = (urlString: string) => {
  try {
    if (validator.isURL(urlString)) {
      return new URL(urlString).hostname;
    }
    return urlString;
  } catch {
    return urlString;
  }
};

/**
 * Capitalizes the first letter of each word in a string.
 *
 * @param input - The string to capitalize
 * @returns The string with first letter of each word capitalized
 *
 * @example
 * capitalizeFirstLetters('hello world') // 'Hello World'
 * capitalizeFirstLetters('HELLO WORLD') // 'Hello World'
 */
export const capitalizeFirstLetters = (input: string) =>
  input
    .toLowerCase()
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.substring(1))
    .join(' ');

/**
 * Converts a price in cents to a dollar string with 2 decimal places.
 *
 * @param price - The price in cents (e.g., 1250 for $12.50)
 * @returns The formatted price string (e.g., '12.50')
 *
 * @example
 * formatPrice(1250) // '12.50'
 * formatPrice(99) // '0.99'
 */
export const formatPrice = (price: number) => (price / 100).toFixed(2);

/**
 * Formats a number as a currency string using USD format.
 *
 * @param input - The number to format as currency
 * @returns The formatted currency string
 *
 * @example
 * formatMoney(12.5) // '$12.50'
 * formatMoney(1000) // '$1,000.00'
 */
export const formatMoney = (input: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });

  return formatter.format(input);
};

/**
 * Normalizes a link by removing protocols and www prefixes.
 * Optionally prepends '//' to allow relative protocol URLs.
 *
 * @param linkRaw - The raw link string to normalize
 * @param addPrefix - If true, prepends '//' to the result (default: true)
 * @returns The normalized link string
 *
 * @example
 * normalizeLink('https://www.example.com') // '//example.com'
 * normalizeLink('http://example.com', false) // 'example.com'
 */
export const normalizeLink = (linkRaw: string, addPrefix = true) => {
  return (
    (addPrefix ? '//' : '') + linkRaw.replace(/^https?:\/\//, '').replace('www.', '')
  );
};

/**
 * Copies a string to the clipboard using the modern Clipboard API with fallback.
 * Falls back to the legacy method if the Clipboard API is not available.
 *
 * @param str - The string to copy to the clipboard
 * @returns A promise that resolves when the copy operation completes
 *
 * @example
 * await copyStringToClipboard('Hello, world!')
 */
export const copyStringToClipboard = async (str: string): Promise<void> => {
  // Modern Clipboard API (preferred method)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(str);
      return;
    } catch (err) {
      // Fall through to legacy method if Clipboard API fails
      if (process.env.NODE_ENV === 'development') {
        console.warn('Clipboard API failed, falling back to legacy method:', err);
      }
    }
  }

  // Legacy fallback method (for older browsers or insecure contexts)
  const el = document.createElement('textarea');
  el.value = str;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);

  // Select and copy
  const selected =
    document.getSelection()?.rangeCount && document.getSelection()?.getRangeAt(0)
      ? document.getSelection()?.getRangeAt(0)
      : false;

  el.select();
  el.setSelectionRange(0, 99999); // For mobile devices

  try {
    document.execCommand('copy');
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to copy to clipboard:', err);
    }
    throw err;
  } finally {
    document.body.removeChild(el);
    // Restore previous selection if any
    if (selected) {
      document.getSelection()?.removeAllRanges();
      document.getSelection()?.addRange(selected);
    }
  }
};
